import React, { useState, useEffect } from "react";
import { Handle, Position } from "reactflow";
import { Trash2, Image, Video, FileText, Music } from "lucide-react";
import { NODE_COLORS, NODE_ICONS } from "../../../utils/builderTokens";
import axiosClient from "../../../api/axiosClient";
import { useAuth } from "../../../app/providers/AuthProvider";
import { BROWSE_CATALOGUE_PATHS } from "../utils/browseCatalogueValidation";
import { SEARCH_CATALOGUE_PATHS } from "../utils/searchCatalogueValidation";
import { PRESENT_CATALOGUE_RESULTS_PATHS } from "../utils/presentCatalogueResultsValidation";
import { PREPARE_COMMERCE_SERVICE_OUTPUTS } from "../utils/prepareCommerceServiceValidation";
import { REVIEW_AND_SUBMIT_PATHS } from "../utils/reviewAndSubmitValidation";
import { WHATSAPP_FLOW_OUTPUTS } from "../utils/whatsAppFlowValidation";
import { DECISION_TABLE_PATHS } from "../utils/decisionTableValidation";
import { DYNAMIC_WHATS_FLOW_OUTPUTS } from "../utils/dynamicWhatsFlowValidation";
import { SCHEDULING_BOOKING_OUTPUTS } from "../utils/schedulingBookingValidation";
import { CONFIRM_BOOKING_OUTPUTS } from "../utils/confirmBookingValidation";
import {
  getUrlCtaHostname,
  URL_CTA_AFTER_SEND,
} from "../utils/urlCtaValidation";

function HeaderMediaPreview({ kind, mediaId, mediaUrl, caption }) {
  // effectiveBusinessId is the single source of truth:
  // - regular users  → derived from JWT claims via /auth/context
  // - SuperAdmin     → the business they have selected in the platform scope
  // Never read from localStorage here; the auth context owns this value.
  const { effectiveBusinessId } = useAuth();
  const [blobUrl, setBlobUrl] = useState(mediaUrl || null);

  useEffect(() => {
    if (mediaUrl) {
      setBlobUrl(mediaUrl);
      return;
    }
    if (!mediaId) return;

    // Primary source: data-URL written to sessionStorage by the editor upload
    // handler immediately after a successful upload. This requires zero network
    // traffic and works across component remounts within the same browser tab.
    const sessionKey = `xbytechat:media-preview:${mediaId}`;
    try {
      const cached = sessionStorage.getItem(sessionKey);
      if (cached) {
        setBlobUrl(cached);
        return;
      }
    } catch {
      // sessionStorage unavailable (private browsing restrictions, etc.)
    }

    // Secondary source: backend proxy endpoint (works for received messages;
    // may also work for uploaded media depending on backend implementation).
    // We gate on effectiveBusinessId so we never fire an unauthenticated request.
    if (!effectiveBusinessId) return;

    let objectUrl;
    axiosClient
      .get(`/chat-inbox/media/${encodeURIComponent(mediaId)}/content`, {
        responseType: "blob",
        __silentToast: true,
        params: { businessId: effectiveBusinessId },
      })
      .then(res => {
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        // Preview unavailable — placeholder is already shown, nothing to do.
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId, mediaUrl, effectiveBusinessId]);

  const iconMap = { image: Image, video: Video, document: FileText, audio: Music };
  const KindIcon = iconMap[kind] || Image;

  return (
    <div className="mb-2 overflow-hidden rounded-lg border border-slate-100">
      {kind === "image" && blobUrl ? (
        <img
          src={blobUrl}
          alt={caption || "Image"}
          className="h-24 w-full object-cover"
        />
      ) : (
        <div className="flex h-16 w-full items-center justify-center gap-2 bg-slate-50">
          <KindIcon className="h-5 w-5 text-purple-400" />
          <span className="text-[10px] font-medium capitalize text-slate-500">
            {kind} attached
          </span>
        </div>
      )}
      {caption && (
        <div className="truncate bg-white px-2 py-1 text-[10px] text-slate-500">
          {caption}
        </div>
      )}
    </div>
  );
}

const basePortStyle = {
  width: 12,
  height: 12,
  border: "2px solid #ffffff",
  borderRadius: "9999px",
  boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.12)",
};

const baseHandleClass = "cursor-default";

function rightPortStyle(color) {
  return {
    ...basePortStyle,
    top: "50%",
    transform: "translateY(-50%)",
    right: -7,
    background: color,
  };
}

export default function AutoReplyNodeBlock({ data, type: nodeType, selected }) {
  const rawType = nodeType || data?.label;
  const type =
    rawType === "choice"
      ? "button_choice"
      : rawType === "condition"
      ? "branch"
      : rawType;
  const config = data?.config || {};
  const prepareProfileId = String(config.commerceOrderPolicyProfileId || "").trim();
  const prepareProfileStatus = data?.commerceOrderPolicyProfileStatus;
  const prepareProfileLabel = !prepareProfileId
    ? "Business default"
    : prepareProfileStatus === "missing"
      ? "Selected policy unavailable"
      : data?.commerceOrderPolicyProfileDisplayName || "Selected policy";
  const validationError =
    String(data?.validationError || "").trim() || "";

  const labelMap = {
    button_choice: "Choice",
    url_cta: "URL Node",
    branch: "Condition",
    decision_table: "Decision Table",
    browse_catalogue: "Browse Catalogue",
    search_catalogue: "Search Catalogue",
    present_catalogue_results: "Present Catalogue Results",
    prepare_commerce_service: "Prepare Commerce Service",
    review_and_submit: "Review & Submit",
    message: "Message",
    input_capture: "Input Capture",
    media: "Media",
    template: "Template",
    wait: "Wait",
    tag: "Tag",
    set_lead_temperature: "Lead Temperature",
    cta_flow: "CTA Flow",
    handover_agent: "Handover Agent",
    whatsapp_flow: "Whats Form",
    whatsapp_flow_dynamic_options: "Dynamic Whats Form",
    schedule_booking: "Schedule Booking",
    confirm_booking: "Confirm Booking",
  };

  const Icon = NODE_ICONS[type] || NODE_ICONS.default;
  const colorKey = NODE_COLORS[type] || NODE_COLORS.default;
  
  // Dynamic color map for the accent strip and selected glow
  const handleColors = {
    emerald: "#10b981",
    blue: "#3b82f6",
    purple: "#a855f7",
    amber: "#f59e0b",
    slate: "#64748b",
    cyan: "#06b6d4",
    green: "#34d399",
    indigo: "#6366f1",
  };
  
  const iconColors = {
    emerald: "text-emerald-600",
    blue: "text-blue-600",
    purple: "text-purple-600",
    amber: "text-amber-600",
    slate: "text-slate-600",
    cyan: "text-cyan-600",
    green: "text-emerald-600",
    indigo: "text-indigo-600"
  };

  const headerBg = {
    emerald: "bg-gradient-to-r from-emerald-50/70 to-white",
    blue:    "bg-gradient-to-r from-blue-50/70 to-white",
    purple:  "bg-gradient-to-r from-purple-50/70 to-white",
    amber:   "bg-gradient-to-r from-amber-50/70 to-white",
    slate:   "bg-gradient-to-r from-slate-100/70 to-white",
    cyan:    "bg-gradient-to-r from-cyan-50/70 to-white",
    green:   "bg-gradient-to-r from-emerald-50/70 to-white",
    indigo:  "bg-gradient-to-r from-indigo-50/70 to-white",
  };
  
  const headerTitle = labelMap[type] || "Block";

  const body = config.body || config.text || "";
  const mediaType = String(config.mediaType || "image").toLowerCase();
  const standaloneMedia =
    type === "media" && (config.mediaId || config.mediaUrl)
      ? {
          kind: mediaType,
          mediaId: config.mediaId,
          mediaUrl: config.mediaUrl,
          caption: mediaType === "audio" ? "" : config.caption,
        }
      : null;
  const buttonChoicePrompt = (config.promptText || config.message || "").toString();
  const headerMedia =
    config.headerMedia && config.headerMedia.kind && (config.headerMedia.mediaId || config.headerMedia.mediaUrl)
      ? config.headerMedia
      : null;
  const buttonChoiceOptions = (Array.isArray(config.options) ? config.options : []).map(
    (opt, index) => ({
      label: String(opt?.label || ""),
      value:
        typeof opt?.value === "string" && opt.value.trim()
          ? opt.value.trim()
          : String(index + 1),
      terms: Array.isArray(opt?.terms)
        ? Array.from(
            new Set(
              opt.terms
                .map(term => String(term || "").trim())
                .filter(Boolean)
            )
          )
        : [],
    })
  );

  const branchInputKey = (config.inputKey || "buttonText").toString();
  const branchPathMap =
    config.pathMap && typeof config.pathMap === "object" && !Array.isArray(config.pathMap)
      ? config.pathMap
      : {};
  const branchEntries = Object.entries(branchPathMap).map(([key, value]) => [
    String(key),
    String(value ?? ""),
  ]);
  const branchRules = Array.isArray(config.rules) ? config.rules : [];
  const isCanonicalBranch = type === "branch" && branchRules.length > 0;

  const branchUnsupportedConfig = type === "branch" && !isCanonicalBranch;

  const buttons = Array.isArray(config.multiButtons) ? config.multiButtons : [];
  const templateName = (config.templateName || "").toString();
  const ctaFlowName = config.ctaFlowName || "";
  const hasTemplateButtons = type === "template" && buttons.length > 0;

  return (
    <div
      className={`group relative min-h-[116px] w-[260px] max-w-[260px] rounded-lg border bg-white p-0 text-[11px] font-normal text-slate-800 transition-all duration-200 hover:-translate-y-0.5 ${
        validationError
          ? "border-red-400 shadow-[0_0_0_3px_rgba(254,226,226,0.95),0_14px_28px_rgba(239,68,68,0.18)]"
          : "border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
      }`}
    >
      {type !== "start" && (
        <Handle
          type="target"
          position={Position.Top}
          className={baseHandleClass}
          style={{
            ...basePortStyle,
            top: -8,
            background: handleColors[colorKey] || "#10b981", // default emerald
          }}
        />
      )}

      {type !== "url_cta" && type !== "button_choice" && type !== "branch" && type !== "decision_table" && type !== "browse_catalogue" && type !== "search_catalogue" && type !== "present_catalogue_results" && type !== "prepare_commerce_service" && type !== "review_and_submit" && type !== "handover_agent" && type !== "whatsapp_flow_dynamic_options" && type !== "schedule_booking" && type !== "confirm_booking" && !hasTemplateButtons && (
        <Handle
          type="source"
          position={Position.Bottom}
          className={baseHandleClass}
          style={{
            ...basePortStyle,
            bottom: -8,
            background: handleColors[colorKey] || "#10b981", // default emerald
          }}
        />
      )}

      {type === "url_cta" && config.afterSend === URL_CTA_AFTER_SEND.CONTINUE && (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable
          aria-label="URL Node output"
          className="nodrag nopan"
          style={{
            ...basePortStyle,
            bottom: -8,
            zIndex: 10,
            cursor: "crosshair",
            background: handleColors[colorKey] || "#10b981",
          }}
        />
      )}

      <div className={`flex items-center justify-between rounded-t-lg pl-4 pr-3 py-3 border-b border-slate-100 ${headerBg[colorKey] || headerBg.slate}`}>
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-200/60">
            <Icon className={`h-3.5 w-3.5 shrink-0 ${iconColors[colorKey] || iconColors.slate}`} strokeWidth={2.5} />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <div className="truncate text-[12px] font-bold tracking-tight text-slate-800">{headerTitle}</div>
            {validationError ? (
              <span className="rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-600">
                Fix
              </span>
            ) : null}
          </div>
        </div>
        {data?.onDelete ? (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              data.onDelete(data.id);
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
            title="Delete block"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      {validationError ? (
        <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-[10px] font-semibold leading-snug text-red-700">
          {validationError}
        </div>
      ) : null}

      <div className="p-3.5 pl-4">

      {type === "template" && (
        <div className="mb-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2 text-[11px] font-medium text-slate-600">
          {templateName || "No template selected"}
        </div>
      )}

      {type === "cta_flow" && (
        <div className="mb-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2 text-[11px] text-slate-600">
          Flow: <span className="font-semibold">{ctaFlowName || "No CTA flow selected"}</span>
        </div>
      )}

      {type === "handover_agent" && (
        <div className="mb-2 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2.5 py-2 text-[11px] text-indigo-700">
          {String(config.ackText || "").trim() || "No pre-handover message"}
        </div>
      )}

      {type === "input_capture" && (
        <div className="space-y-1.5">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] text-slate-700">
            {String(config.question || "").trim() || "Please enter your response:"}
          </div>
          <div className="truncate text-[10px] text-slate-500">
            {String(config.fieldKey || "custom.response")} · {String(config.inputType || "text")}
          </div>
        </div>
      )}

      {type === "url_cta" && (
        <div className="space-y-2">
          <div className="whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2 text-[11px] text-slate-700">
            {String(config.body || "").trim() || "Add message text"}
          </div>
          <div className="rounded-lg border border-indigo-100 bg-white px-2.5 py-2 text-center text-[11px] font-semibold text-indigo-600">
            {String(config.buttonLabel || "").trim() || "Visit Website"}
          </div>
          {getUrlCtaHostname(config.url) ? (
            <div className="truncate text-[10px] text-slate-500">
              {getUrlCtaHostname(config.url)}
            </div>
          ) : null}
          <div className="text-[10px] font-medium text-slate-500">
            {config.afterSend === URL_CTA_AFTER_SEND.END ? "End" : "Continue"}
          </div>
        </div>
      )}

      {type === "browse_catalogue" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] text-slate-700">
            <div className="truncate font-semibold">
              {String(config.promptText || "").trim() ||
                "Please choose a category."}
            </div>
            <div className="mt-1 text-[10px] capitalize text-slate-500">
              {config.catalogId ? "Selected catalogue" : "Default catalogue"} · {String(config.presentation || "auto")}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {BROWSE_CATALOGUE_PATHS.map(path => (
              <div
                key={path.id}
                className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"
              >
                <span className="truncate">{path.label}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={path.id}
                  aria-label={`${path.label} output`}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#06b6d4")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "search_catalogue" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] text-slate-700">
            <div className="truncate font-semibold">
              {String(config.searchMode || "filtered") === "all"
                ? "All catalogue products"
                : String(config.queryFieldKey || "").trim() ||
                String(data?.browseSourceLabel || "").trim() ||
                (config.categorySourceNodeId
                  ? "Browse Catalogue source"
                  : "Select a query or category source")}
            </div>
            <div className="mt-1 text-[10px] text-slate-500">
              {config.catalogId ? "Selected catalogue" : "Default catalogue"} · Max {Number(config.maximumResults) || 5}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {SEARCH_CATALOGUE_PATHS.map(path => (
              <div
                key={path.id}
                className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"
              >
                <span className="truncate">{path.label}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={path.id}
                  aria-label={`${path.label} output`}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#06b6d4")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "present_catalogue_results" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] text-slate-700">
            <div className="truncate font-semibold">
              {String(data?.searchNodeLabel || config.searchNodeId || "").trim() ||
                "Select Search Catalogue source"}
            </div>
            <div className="mt-1 truncate text-[10px] text-slate-500">
              {String(config.promptText ?? "").trim() ||
                "Please select an option."}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {PRESENT_CATALOGUE_RESULTS_PATHS.map(path => (
              <div
                key={path.id}
                className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"
              >
                <span className="truncate">{path.label}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={path.id}
                  aria-label={`${path.label} output`}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#06b6d4")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "review_and_submit" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] text-slate-700">
            <div className="truncate font-semibold">
              {String(config.promptText || "").trim() ||
                "Please review your request."}
            </div>
            <div className="mt-1 text-[10px] text-slate-500">
              {Array.isArray(config.detailBindings)
                ? `${config.detailBindings.length} details included`
                : "0 details included"}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {REVIEW_AND_SUBMIT_PATHS.map(path => (
              <div
                key={path.id}
                className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"
              >
                <span className="truncate">{path.label}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={path.id}
                  aria-label={`${path.label} output`}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#06b6d4")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "whatsapp_flow" && (
        <div className="space-y-2">
          <div className={`rounded-lg border px-2.5 py-2 text-[11px] ${
            config.flowDefinitionId && config.flowRevisionId
              ? "border-emerald-100 bg-emerald-50/50 text-slate-700"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }`}>
            <div className="truncate font-semibold">
              {data?.whatsAppFlowDisplayName ||
                (config.flowDefinitionId ? "Configured WhatsApp Form" : "Configure this Whats Form")}
            </div>
            <div className="mt-1 flex items-center justify-between gap-2 text-[10px]">
              <span className="rounded bg-white px-1.5 py-0.5 text-emerald-700">Endpointless</span>
              <span className="text-slate-500">
                {Array.isArray(config.fieldMappings)
                  ? `${config.fieldMappings.filter(mapping => mapping?.fieldId).length} mapped`
                  : "0 mapped"}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {WHATSAPP_FLOW_OUTPUTS.map(path => (
              <div key={path.id} className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
                <span className="truncate">{path.label}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={path.id}
                  aria-label={`${path.label} output`}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#10b981")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "whatsapp_flow_dynamic_options" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] font-semibold text-slate-700">
            {(config.dependencies || []).length} dynamic dependencies · pinned revision
          </div>
          <div className="flex flex-col gap-1.5">
            {DYNAMIC_WHATS_FLOW_OUTPUTS.map(path => (
              <div key={path.id} className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
                <span>{path.label}</span>
                <Handle type="source" position={Position.Right} id={path.id} aria-label={`${path.label} output`} className={baseHandleClass} style={{ ...basePortStyle, right: -16 }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "schedule_booking" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-2.5 py-2 text-[11px] font-semibold text-slate-700">
            {data?.schedulingDisplayName || "Pinned Scheduling Offering"} · Data Exchange
          </div>
          <div className="flex flex-col gap-1.5">
            {SCHEDULING_BOOKING_OUTPUTS.map(path => <div key={path.id} className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
              <span>{path.label}</span><Handle type="source" position={Position.Right} id={path.id} aria-label={`${path.label} output`} className={baseHandleClass} style={{ ...basePortStyle, right: -16 }} />
            </div>)}
          </div>
        </div>
      )}

      {type === "prepare_commerce_service" && <div className="space-y-2">
        <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 px-2.5 py-2 text-[11px] font-semibold text-slate-700">
          <div>Scheduling Offering → Commerce Booking</div>
          <div className={`mt-1 truncate font-medium ${prepareProfileStatus === "inactive" || prepareProfileStatus === "missing" ? "text-rose-700" : "text-slate-500"}`}>
            Policy: {prepareProfileLabel}{prepareProfileStatus === "inactive" ? " (Inactive)" : ""}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">{PREPARE_COMMERCE_SERVICE_OUTPUTS.map(path => <div key={path.id} className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"><span>{path.label}</span><Handle type="source" position={Position.Right} id={path.id} aria-label={`${path.label} output`} className={baseHandleClass} style={{ ...basePortStyle, right: -16 }} /></div>)}</div>
      </div>}

      {type === "confirm_booking" && <div className="space-y-2">
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-2.5 py-2 text-[11px] font-semibold text-slate-700">Held selection → confirmed Booking</div>
        <div className="flex flex-col gap-1.5">{CONFIRM_BOOKING_OUTPUTS.map(path => <div key={path.id} className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"><span>{path.label}</span><Handle type="source" position={Position.Right} id={path.id} aria-label={`${path.label} output`} className={baseHandleClass} style={{ ...basePortStyle, right: -16 }} /></div>)}</div>
      </div>}

      {type === "media" && (
        <>
          {standaloneMedia ? (
            <HeaderMediaPreview {...standaloneMedia} />
          ) : (
            <div className="mb-2 rounded-lg border border-dashed border-purple-200 bg-purple-50/40 px-2.5 py-3 text-center text-[10px] font-medium text-purple-500">
              No media selected
            </div>
          )}
          <div className="text-[10px] capitalize text-slate-500">
            {mediaType}
            {mediaType === "document" && config.filename ? ` · ${config.filename}` : ""}
          </div>
        </>
      )}

      {body && type !== "url_cta" && type !== "cta_flow" && type !== "button_choice" && type !== "branch" && type !== "decision_table" && type !== "browse_catalogue" && type !== "search_catalogue" && type !== "present_catalogue_results" && type !== "prepare_commerce_service" && type !== "review_and_submit" && type !== "handover_agent" && type !== "input_capture" && type !== "whatsapp_flow" && type !== "whatsapp_flow_dynamic_options" && type !== "schedule_booking" && type !== "confirm_booking" && (
        <div
          className={`ar-node-body-scroll max-h-24 overflow-y-auto whitespace-pre-wrap px-2 py-1.5 pr-1 text-[11px] font-normal leading-relaxed text-slate-700 ${
            type === "message" ? "" : "rounded-lg border border-slate-100 bg-slate-50"
          }`}
        >
          {body}
        </div>
      )}

      {!body && type === "message" && (
        <div className="px-2 py-2 text-[10px] font-medium text-slate-400">
          Type your response message...
        </div>
      )}

      {type === "button_choice" && (
        <>
          {headerMedia && (
            <HeaderMediaPreview
              kind={headerMedia.kind}
              mediaId={headerMedia.mediaId}
              mediaUrl={headerMedia.mediaUrl}
              caption={headerMedia.caption}
            />
          )}
          <div className="ar-node-body-scroll max-h-24 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5 pr-1 text-[11px] font-normal leading-relaxed text-slate-700">
            {buttonChoicePrompt || "Please choose an option:"}
          </div>
          {buttonChoiceOptions.length > 0 && (
            <div className="mt-2 flex flex-col gap-1.5">
              {buttonChoiceOptions.map((opt, index) => (
                <div
                  key={index}
                  className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-colors hover:border-emerald-200 hover:bg-emerald-50/30"
                >
                  <div className="flex min-w-0 flex-1 flex-col justify-center">
                    <span className="block truncate">
                      {index + 1}. {opt?.label || "(unnamed)"}
                    </span>
                    {opt.terms?.length > 0 && (
                      <span className="block truncate text-[9px] font-normal text-slate-400">
                        {opt.terms.join(", ")}
                      </span>
                    )}
                  </div>

                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`opt-${index}`}
                    className={baseHandleClass}
                    style={rightPortStyle(handleColors[colorKey] || "#10b981")}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {type === "branch" && (
        <>
          <div className="ar-node-body-scroll max-h-20 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5 pr-1 text-[11px] font-medium leading-relaxed text-slate-700">
            Branch by <span className="font-bold text-slate-800">{isCanonicalBranch ? config.fieldKey : branchInputKey}</span>
          </div>
          {branchUnsupportedConfig && (
            <div className="mt-1 text-[10px] text-amber-600">Unsupported config</div>
          )}

          <div className="mt-2 flex flex-col gap-1.5">
            {(isCanonicalBranch
              ? branchRules.map(rule => [rule.pathId, rule.label])
              : branchEntries
            ).map(([key, value]) => (
              <div
                key={key}
                className="relative flex min-h-[26px] items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-colors hover:border-emerald-200 hover:bg-emerald-50/30"
              >
                <div className="flex min-w-0 items-center gap-1.5 text-slate-700">
                  <span className="truncate rounded bg-slate-100 px-1 py-0.5 text-[9px] font-bold text-slate-500">{key}</span>
                  <span className="text-slate-400">→</span>
                  <span className="truncate">{value}</span>
                </div>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={key}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#10b981")}
                />
              </div>
            ))}

            <div className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 border-dashed bg-slate-50/50 px-2.5 py-1.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-emerald-200 hover:bg-emerald-50/30">
              <span className="block truncate italic">default fallback</span>
              <Handle
                type="source"
                position={Position.Right}
                id="default"
                className={baseHandleClass}
                style={rightPortStyle("#94a3b8")}
              />
            </div>
          </div>
        </>
      )}

      {type === "decision_table" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-2.5 py-2 text-[11px] font-semibold text-slate-700">
            {(config.inputs || []).length} inputs · {(config.rows || []).length} rules · {(config.outputs || []).length} outputs
          </div>
          <div className="flex flex-col gap-1.5">
            {DECISION_TABLE_PATHS.map(path => (
              <div key={path.id} className="relative flex min-h-[26px] items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
                <span className="truncate">{path.label}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={path.id}
                  aria-label={`${path.label} output`}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#f59e0b")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "template" && buttons.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {buttons.map((btn, index) => (
            <div
              key={index}
              className="relative flex min-h-[26px] items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center text-[11px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-colors hover:border-emerald-200 hover:bg-emerald-50/30"
            >
              {btn.buttonText || btn.text || "(unnamed)"}
              <Handle
                type="source"
                position={Position.Right}
                id={`button-${index}`}
                className={baseHandleClass}
                style={rightPortStyle(handleColors[colorKey] || "#10b981")}
              />
            </div>
          ))}
        </div>
      )}

      {type === "set_lead_temperature" && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px] text-slate-700">
          Set to <span className="font-medium">{config.leadTemperature || "Choose temperature"}</span>
          {config.allowTemperatureDowngrade && <span className="block mt-1 text-slate-500">Lowering temperature allowed</span>}
        </div>
      )}
      {type === "tag" && config.tags?.length > 0 && (
        <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50/50 px-2.5 py-2 text-[11px] text-emerald-700">
          <span className="font-semibold">Tags:</span> {config.tags.join(", ")}
        </div>
      )}

      {type === "wait" && config.seconds && (
        <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50/50 px-2.5 py-2 text-[11px] text-amber-700">
          Wait for <span className="font-bold">{config.seconds}s</span>
        </div>
      )}
      </div>
    </div>
  );
}
